import { requireOrdinaryEntityType } from '@einhaus/project314-shared';
import type { Dispatch, SetStateAction } from 'react';
import Button from '@components/UI/Button';
import Modal from '@components/UI/Modal';
import FormNumberInput from '@components/UI/FormNumberInput';
import FormSelect from '@components/UI/FormSelect';
import { showToast } from '@components/Layout/Toast';
import { showSpinner, hideSpinner } from '@components/Layout/LoadingSpinner';
import type { EntityTypeUnion, TradeDirection, TradeFilter, TradingProfile } from '@einhaus/project314-types';
import { getUnixTime } from '@einhaus/meteoric-helpers';
import type { NewTradeFilterState, SaveTradingProfileSettings } from './types';

interface TradeFilterModalProps {
    isOpen: boolean;
    setShowTradeFilterModal: Dispatch<SetStateAction<boolean>>;
    editingTradeFilterId: string | null;
    setEditingTradeFilterId: Dispatch<SetStateAction<string | null>>;
    newTradeFilter: NewTradeFilterState;
    setNewTradeFilter: Dispatch<SetStateAction<NewTradeFilterState>>;
    tradeFilters: TradeFilter[];
    setTradeFilters: Dispatch<SetStateAction<TradeFilter[]>>;
    formData: TradingProfile;
    setFormData: Dispatch<SetStateAction<TradingProfile>>;
    tradingProfileId: number;
    saveTradeFilter: SaveTradingProfileSettings;
}

const TradeFilterModal = ({
    isOpen,
    setShowTradeFilterModal,
    editingTradeFilterId,
    setEditingTradeFilterId,
    newTradeFilter,
    setNewTradeFilter,
    tradeFilters,
    setTradeFilters,
    formData,
    setFormData,
    tradingProfileId,
    saveTradeFilter
}: TradeFilterModalProps) => {
    // Trade Filter Modal handlers
    const closeTradeFilterModal = () => {
        setShowTradeFilterModal(false);
        setEditingTradeFilterId(null);

        setNewTradeFilter({
            entityType: 'Asset',
            tradeDirection: 'either',
            property: '',
            comparison: 'greaterThan',
            value: null
        });
    };

    const handleSaveTradeFilter = () => {
        const { entityType, tradeDirection, property, comparison, value } = newTradeFilter;

        if (!entityType || !property || !comparison || value === null) {
            showToast('Please fill in all trade filter fields', 'error');
            return;
        }

        const filterId = editingTradeFilterId ?? getUnixTime().toString();

        const newFilter: TradeFilter = {
            id: filterId,
            value,
            field: property,
            operator: comparison as 'greaterThan' | 'lessThan' | 'greaterThanEqual' | 'lessThanEqual' | 'equal',
            tradeDirection: tradeDirection as TradeDirection | 'either',
            entityType: requireOrdinaryEntityType(entityType)
        };

        const updatedTradeFilters =
            editingTradeFilterId == null
                ? [...tradeFilters, newFilter]
                : tradeFilters.map((existing) => (existing.id === filterId ? newFilter : existing));

        setTradeFilters(updatedTradeFilters);

        // Update the form data with the new trade filter
        const updatedFormData = {
            ...formData,
            tradeFilters: updatedTradeFilters
        };

        setFormData(updatedFormData);
        showSpinner();

        // Use the trade filter mutation to save the updated settings
        saveTradeFilter(
            {
                tradingProfileId,
                settings: updatedFormData
            },
            {
                onSuccess: () => {
                    hideSpinner();
                    closeTradeFilterModal();
                },
                onError: () => {
                    hideSpinner();
                }
            }
        );
    };

    return (
        <Modal
            footer={
                <>
                    <Button variant="secondary" onClick={closeTradeFilterModal}>
                        Cancel
                    </Button>
                    <Button variant="primary" onClick={handleSaveTradeFilter}>
                        {editingTradeFilterId == null ? 'Add' : 'Save'}
                    </Button>
                </>
            }
            isOpen={isOpen}
            title={editingTradeFilterId == null ? 'Add Trade Filter' : 'Edit Trade Filter'}
            onClose={closeTradeFilterModal}
        >
            <div className="mb-4">
                <label className="block text-sm font-medium text-zinc-200 mb-1" htmlFor="entityTypeSelect">
                    Entity Type
                </label>
                <FormSelect
                    className="w-full bg-zinc-800 border border-zinc-600 rounded px-3 py-2 text-white focus:outline-none focus:ring-2 focus:ring-blue-500"
                    id="entityTypeSelect"
                    options={[
                        { value: 'Asset', label: 'Asset' },
                        { value: 'Cryptocurrency', label: 'Cryptocurrency' },
                        { value: 'AssetIndex', label: 'Asset Index' }
                    ]}
                    value={newTradeFilter.entityType}
                    onChange={(e) =>
                        setNewTradeFilter((prev) => ({ ...prev, entityType: e.target.value as EntityTypeUnion['entityType'] }))
                    }
                />
            </div>
            <div className="mb-4">
                <label className="block text-sm font-medium text-zinc-200 mb-1" htmlFor="tradeDirectionSelect">
                    Position side
                </label>
                <FormSelect
                    className="w-full bg-zinc-800 border border-zinc-600 rounded px-3 py-2 text-white focus:outline-none focus:ring-2 focus:ring-blue-500"
                    id="tradeDirectionSelect"
                    options={[
                        { value: 'either', label: 'Either' },
                        { value: 'long', label: 'Long' },
                        { value: 'short', label: 'Short' }
                    ]}
                    value={newTradeFilter.tradeDirection}
                    onChange={(e) => setNewTradeFilter((prev) => ({ ...prev, tradeDirection: e.target.value }))}
                />
            </div>
            <div className="mb-3">
                <label className="block text-sm font-medium text-zinc-200 mb-1" htmlFor="filterSelect">
                    Property
                </label>
                <select
                    className="w-full bg-zinc-800 border border-zinc-600 rounded px-3 py-2 text-white focus:outline-none focus:ring-2 focus:ring-blue-500"
                    id="filterSelect"
                    value={newTradeFilter.property}
                    onChange={(e) => setNewTradeFilter((prev) => ({ ...prev, property: e.target.value }))}
                >
                    <option value="">Choose:</option>
                    <optgroup label="General Metrics">
                        <option value="avgMonth">Average Month</option>
                        <option value="numTrades">Number of Trades</option>
                        <option value="numTradesLong">Number of Long Trades</option>
                        <option value="numTradesShort">Number of Short Trades</option>
                        <option value="avgTradeProfit">Average Trade Return (%)</option>
                        <option value="avgTradeProfitShort">Average Short Trade Return (%)</option>
                        <option value="avgTradeProfitLong">Average Long Trade Return (%)</option>
                        <option value="profitFactor">Profit Factor</option>
                        <option value="medianProfitPercent">Median Trade Return (%)</option>
                        <option value="medianProfitLongPercent">Median Long Trade Return (%)</option>
                        <option value="medianProfitShortPercent">Median Short Trade Return (%)</option>
                        <option value="returnOnRiskCapital">Return on Risk Capital (%)</option>
                        <option value="avgProfitPerDayPercent">Average Profit/Day (%)</option>
                        <option value="medianProfitPerDayPercent">Median Profit/Day (%)</option>
                        <option value="distinctTradingDays">Distinct Trading Days</option>
                        <option value="activeMonths">Active Months</option>
                        <option value="winRate">Win Rate</option>
                        <option value="winRateLong">Long Win Rate</option>
                        <option value="winRateShort">Short Win Rate</option>
                        <option value="worstMonth">Worst Month</option>
                    </optgroup>

                    <optgroup label="7-Day Metrics">
                        <option value="numTrades7">Number of Trades (7d)</option>
                        <option value="avgGain7">Average Gain (7d)</option>
                        <option value="winRate7">Win Rate (7d)</option>
                        <option value="numTradesLong7">Number of Long Trades (7d)</option>
                        <option value="avgGainLong7">Average Long Gain (7d)</option>
                        <option value="winRateLong7">Long Win Rate (7d)</option>
                        <option value="numTradesShort7">Number of Short Trades (7d)</option>
                        <option value="avgGainShort7">Average Short Gain (7d)</option>
                        <option value="winRateShort7">Short Win Rate (7d)</option>
                    </optgroup>

                    <optgroup label="14-Day Metrics">
                        <option value="numTrades14">Number of Trades (14d)</option>
                        <option value="avgGain14">Average Gain (14d)</option>
                        <option value="winRate14">Win Rate (14d)</option>
                        <option value="numTradesLong14">Number of Long Trades (14d)</option>
                        <option value="avgGainLong14">Average Long Gain (14d)</option>
                        <option value="winRateLong14">Long Win Rate (14d)</option>
                        <option value="numTradesShort14">Number of Short Trades (14d)</option>
                        <option value="avgGainShort14">Average Short Gain (14d)</option>
                        <option value="winRateShort14">Short Win Rate (14d)</option>
                    </optgroup>

                    <optgroup label="30-Day Metrics">
                        <option value="numTrades30">Number of Trades (30d)</option>
                        <option value="avgGain30">Average Gain (30d)</option>
                        <option value="winRate30">Win Rate (30d)</option>
                        <option value="numTradesLong30">Number of Long Trades (30d)</option>
                        <option value="avgGainLong30">Average Long Gain (30d)</option>
                        <option value="winRateLong30">Long Win Rate (30d)</option>
                        <option value="numTradesShort30">Number of Short Trades (30d)</option>
                        <option value="avgGainShort30">Average Short Gain (30d)</option>
                        <option value="winRateShort30">Short Win Rate (30d)</option>
                    </optgroup>

                    <optgroup label="60-Day Metrics">
                        <option value="numTrades60">Number of Trades (60d)</option>
                        <option value="avgGain60">Average Gain (60d)</option>
                        <option value="winRate60">Win Rate (60d)</option>
                        <option value="numTradesLong60">Number of Long Trades (60d)</option>
                        <option value="avgGainLong60">Average Long Gain (60d)</option>
                        <option value="winRateLong60">Long Win Rate (60d)</option>
                        <option value="numTradesShort60">Number of Short Trades (60d)</option>
                        <option value="avgGainShort60">Average Short Gain (60d)</option>
                        <option value="winRateShort60">Short Win Rate (60d)</option>
                    </optgroup>

                    <optgroup label="90-Day Metrics">
                        <option value="numTrades90">Number of Trades (90d)</option>
                        <option value="avgGain90">Average Gain (90d)</option>
                        <option value="winRate90">Win Rate (90d)</option>
                        <option value="numTradesLong90">Number of Long Trades (90d)</option>
                        <option value="avgGainLong90">Average Long Gain (90d)</option>
                        <option value="winRateLong90">Long Win Rate (90d)</option>
                        <option value="numTradesShort90">Number of Short Trades (90d)</option>
                        <option value="avgGainShort90">Average Short Gain (90d)</option>
                        <option value="winRateShort90">Short Win Rate (90d)</option>
                    </optgroup>

                    <optgroup label="120-Day Metrics">
                        <option value="numTrades120">Number of Trades (120d)</option>
                        <option value="avgGain120">Average Gain (120d)</option>
                        <option value="winRate120">Win Rate (120d)</option>
                        <option value="numTradesLong120">Number of Long Trades (120d)</option>
                        <option value="avgGainLong120">Average Long Gain (120d)</option>
                        <option value="winRateLong120">Long Win Rate (120d)</option>
                        <option value="numTradesShort120">Number of Short Trades (120d)</option>
                        <option value="avgGainShort120">Average Short Gain (120d)</option>
                        <option value="winRateShort120">Short Win Rate (120d)</option>
                    </optgroup>

                    <optgroup label="180-Day Metrics">
                        <option value="numTrades180">Number of Trades (180d)</option>
                        <option value="avgGain180">Average Gain (180d)</option>
                        <option value="winRate180">Win Rate (180d)</option>
                        <option value="numTradesLong180">Number of Long Trades (180d)</option>
                        <option value="avgGainLong180">Average Long Gain (180d)</option>
                        <option value="winRateLong180">Long Win Rate (180d)</option>
                        <option value="numTradesShort180">Number of Short Trades (180d)</option>
                        <option value="avgGainShort180">Average Short Gain (180d)</option>
                        <option value="winRateShort180">Short Win Rate (180d)</option>
                    </optgroup>

                    <optgroup label="365-Day Metrics">
                        <option value="numTrades365">Number of Trades (365d)</option>
                        <option value="avgGain365">Average Gain (365d)</option>
                        <option value="winRate365">Win Rate (365d)</option>
                        <option value="numTradesLong365">Number of Long Trades (365d)</option>
                        <option value="avgGainLong365">Average Long Gain (365d)</option>
                        <option value="winRateLong365">Long Win Rate (365d)</option>
                        <option value="numTradesShort365">Number of Short Trades (365d)</option>
                        <option value="avgGainShort365">Average Short Gain (365d)</option>
                        <option value="winRateShort365">Short Win Rate (365d)</option>
                    </optgroup>

                    <optgroup label="545-Day Metrics">
                        <option value="numTrades545">Number of Trades (545d)</option>
                        <option value="avgGain545">Average Gain (545d)</option>
                        <option value="winRate545">Win Rate (545d)</option>
                        <option value="numTradesLong545">Number of Long Trades (545d)</option>
                        <option value="avgGainLong545">Average Long Gain (545d)</option>
                        <option value="winRateLong545">Long Win Rate (545d)</option>
                        <option value="numTradesShort545">Number of Short Trades (545d)</option>
                        <option value="avgGainShort545">Average Short Gain (545d)</option>
                        <option value="winRateShort545">Short Win Rate (545d)</option>
                    </optgroup>

                    <optgroup label="730-Day Metrics">
                        <option value="numTrades730">Number of Trades (730d)</option>
                        <option value="avgGain730">Average Gain (730d)</option>
                        <option value="winRate730">Win Rate (730d)</option>
                        <option value="numTradesLong730">Number of Long Trades (730d)</option>
                        <option value="avgGainLong730">Average Long Gain (730d)</option>
                        <option value="winRateLong730">Long Win Rate (730d)</option>
                        <option value="numTradesShort730">Number of Short Trades (730d)</option>
                        <option value="avgGainShort730">Average Short Gain (730d)</option>
                        <option value="winRateShort730">Short Win Rate (730d)</option>
                    </optgroup>
                </select>
            </div>
            <div className="mb-3">
                <label className="block text-sm font-medium text-zinc-200 mb-1" htmlFor="operatorSelect">
                    Operator
                </label>
                <FormSelect
                    className="w-full bg-zinc-800 border border-zinc-600 rounded px-3 py-2 text-white focus:outline-none focus:ring-2 focus:ring-blue-500"
                    id="operatorSelect"
                    options={[
                        { value: 'greaterThan', label: 'Greater Than (>)' },
                        { value: 'lessThan', label: 'Less Than (<)' },
                        { value: 'greaterThanEqual', label: 'Greater Than or Equal (>=)' },
                        { value: 'lessThanEqual', label: 'Less Than or Equal (<=)' },
                        { value: 'equal', label: 'Equal (=)' }
                    ]}
                    value={newTradeFilter.comparison}
                    onChange={(e) => setNewTradeFilter((prev) => ({ ...prev, comparison: e.target.value }))}
                />
            </div>
            <div className="mb-3">
                <label className="block text-sm font-medium text-zinc-200 mb-1" htmlFor="filterValue">
                    Value
                </label>
                <FormNumberInput
                    id="filterValue"
                    placeholder="Enter value"
                    value={newTradeFilter.value}
                    onValueChange={(value) => setNewTradeFilter((prev) => ({ ...prev, value }))}
                />
            </div>
        </Modal>
    );
};

export default TradeFilterModal;
